import clsx from "clsx";
import { useState } from "react";

import { DrawerHeader, DrawerTitle } from "common/components/ui/Drawer";
import { typedObjectEntries } from "common/helpers/utils";
import type { ActionsLeafKey, ActionsType } from "common/types";

interface MobileActionsProps<EditOptions extends ActionsType> {
  editOptions: EditOptions;
  children: (
    editKey: ActionsLeafKey<EditOptions>,
    onBack: () => void,
  ) => JSX.Element;
  editTitle: string;
}

export default function MobileActions<EditOptions extends ActionsType>({
  editOptions,
  children,
  editTitle,
}: MobileActionsProps<EditOptions>) {
  const [editKey, setEditKey] = useState<ActionsLeafKey<EditOptions>>();
  const [subActionsKey, setSubActionsKey] = useState<keyof EditOptions>();

  if (editKey) {
    return children(editKey, () => {
      setEditKey(undefined);
    });
  }

  if (subActionsKey) {
    const { label, subActions } = editOptions[subActionsKey];

    return (
      <>
        <DrawerHeader
          onBack={() => setSubActionsKey(undefined)}
          title={label}
        />
        <div className="flex flex-col">
          {typedObjectEntries(subActions ?? {}).map(
            ([subKey, { icon: Icon, label, isDanger, onAction }]) => (
              <button
                key={String(subKey)}
                type="button"
                onClick={() =>
                  onAction
                    ? onAction()
                    : setEditKey(subKey as ActionsLeafKey<EditOptions>)
                }
                className={clsx(
                  "flex items-center p-2 border-b border-b-black-100 last:border-b-0 gap-x-1.5 text-button-sm",
                  isDanger ? "text-red-500" : "",
                )}
              >
                <Icon className="size-6" />
                {label}
              </button>
            ),
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <DrawerTitle>{editTitle}</DrawerTitle>
      <div className="flex flex-col">
        {typedObjectEntries(editOptions).map(
          ([key, { icon: Icon, label, isDanger, subActions, onAction }]) => {
            return (
              <button
                key={String(key)}
                type="button"
                onClick={() => {
                  if (onAction) {
                    onAction();
                  } else if (subActions) {
                    setSubActionsKey(key);
                  } else {
                    setEditKey(key as ActionsLeafKey<EditOptions>);
                  }
                }}
                className={clsx(
                  "flex items-center p-2 border-b border-b-black-100 last:border-b-0 gap-x-1.5 text-button-sm",
                  isDanger ? "text-red-500" : "",
                )}
              >
                <Icon className="size-6" />
                {label}
              </button>
            );
          },
        )}
      </div>
    </>
  );
}
